import getExclusionReason from '@app.raytd.com/common/utils/tests/getExclusionReason';
import { toast } from 'sonner';
import { Assessment } from '../assessment/assessment.slice';
import { AssetEntity } from '../asset';
import { TestCase, TestItem, TestSuite } from '../tests/entity';
import type { ReferenceImage, ReferenceImageDisposition } from '../tests/api/referenceImages';
import { TestSpecificationsEntity } from '../tests/test-specifications.slice';
import { UsersEntity } from '../users';
import { transformClassificationTree } from './elementTransformer';
import {
  ActivityItem,
  AddCustomFieldOptionRequest,
  AddCustomFieldOptionResponse,
  ClassificationItem,
  DashboardRecentResponse,
  FastFillUpdateRequest,
  GetTestItemRequest,
  PublishedReportResponse,
  PublishReportPayload,
  PublishReportResponse,
  ReportBlock,
  ReportEntity,
  ReportPayload,
  ReportResponse,
  ReportRevision,
  ReportSection,
  ReportTestItem,
  ReportUpdatePayload,
  ToggleSwitchRequest,
  ToggleSwitchResponse,
  TestItemComment,
} from './types';
import updateItemInReportCache from './updateItemInReportCache';
import {
  NotificationPagination,
  AppNotification,
  ReportExport,
} from '../notifications/types';
import { rAPI } from './rAPI';
import { inspectionsReportsAPI } from './inspectionsReportsAPI';

export const testItemsAPI = rAPI.injectEndpoints({
  overrideExisting: false,
  endpoints: (builder) => ({
    getTestItem: builder.query({
      query: (arg: number | GetTestItemRequest) => {
        // Support both number (legacy) and object syntax
        const testItemId = typeof arg === 'number' ? arg : arg.testItemId;
        const reportId =
          typeof arg === 'object' && arg.reportId ? arg.reportId : undefined;

        const params = new URLSearchParams();
        if (reportId) {
          params.append('reportId', String(reportId));
        }

        const queryString = params.toString();
        return `tests/${testItemId}${queryString ? `?${queryString}` : ''}`;
      },
      transformResponse: (response: { data: any }) => response.data,
      providesTags: (result) => {
        return result
          ? [
              { type: 'TestItem' as const, id: result.assessmentTestId },
              { type: 'TestItem', id: 'LIST' },
            ]
          : [{ type: 'TestItem', id: 'LIST' }];
      },
    }),

    getInspectionTestItems: builder.query<ReportTestItem[], string>({
      query: (inspectionId) =>
        `assessments/${inspectionId}/test-items?format=report`,
      transformResponse: (response: { data: ReportTestItem[] }) =>
        response.data,
      providesTags: (result, error, inspectionId) =>
        result
          ? [
              ...result.map(({ assessmentTestId }) => ({
                type: 'TestItem' as const,
                id: assessmentTestId,
              })),
              { type: 'TestItems', id: `inspection-${inspectionId}` },
              { type: 'TestItems', id: 'LIST' },
            ]
          : [
              { type: 'TestItems', id: `inspection-${inspectionId}` },
              { type: 'TestItems', id: 'LIST' },
            ],
    }),

    createTestItem: builder.mutation({
      query: (newTestItem) => ({
        url: 'tests',
        method: 'POST',
        body: newTestItem,
      }),
      invalidatesTags: (result, error, { assessmentId }) => [
        { type: 'TestItems', id: 'LIST' },
        { type: 'TestItems', id: `inspection-${assessmentId}` },
        { type: 'TestItem', id: 'LIST' },
      ],
    }),

    updateTestItem: builder.mutation<TestItem, Partial<TestItem>>({
      query: ({ assessmentTestId, ...updatedItem }) => ({
        url: `tests/${assessmentTestId}`,
        method: 'PUT',
        body: updatedItem,
      }),
      invalidatesTags: (result, error, { assessmentTestId, assessmentId }) => [
        { type: 'TestItems', id: `inspection-${assessmentId}` },
        { type: 'TestItem', id: assessmentTestId },
        { type: 'TestItem', id: 'LIST' },
      ],
      async onQueryStarted(
        { assessmentTestId, assessmentId, ...patch },
        { dispatch, queryFulfilled }
      ) {
        // Optimistically update the 'getTestItem' cache
        const getTestItemPatch = dispatch(
          testItemsAPI.util.updateQueryData(
            'getTestItem',
            assessmentTestId,
            (draft: ReportTestItem) => {
              Object.assign(draft, patch);
            }
          )
        );

        const patchResult = dispatch(
          testItemsAPI.util.updateQueryData(
            'getInspectionTestItems',
            String(assessmentId),
            (draft: ReportTestItem[]) => {
              const itemToUpdate = draft.find(
                (item) => item.assessmentTestId === assessmentTestId
              );
              if (itemToUpdate) {
                Object.assign(itemToUpdate, patch);
              }
            }
          )
        );

        try {
          const { data: updatedItem } = await queryFulfilled;

          // On success, update the 'getTestItem' cache with the final server data
          dispatch(
            testItemsAPI.util.updateQueryData(
              'getTestItem',
              assessmentTestId,
              (draft: ReportTestItem) => {
                Object.assign(draft, updatedItem);
              }
            )
          );

          // On success, update the cache with the final data from the server response
          dispatch(
            testItemsAPI.util.updateQueryData(
              'getInspectionTestItems',
              String(assessmentId),
              (draft: ReportTestItem[]) => {
                const itemToUpdate = draft.find(
                  (item) => item.assessmentTestId === assessmentTestId
                );
                if (itemToUpdate) {
                  // Update the item with the complete, returned data
                  Object.assign(itemToUpdate, updatedItem);
                }
              }
            )
          );
        } catch {
          getTestItemPatch.undo();
          // If the mutation fails, undo the optimistic patch
          patchResult.undo();
        }
      },
    }),

    updateReportTestItem: builder.mutation<
      ReportTestItem,
      Partial<TestItem & { reportId: number }>
    >({
      query: ({ reportId, assessmentTestId, ...updatedItem }) => ({
        url: `reports/${reportId}/test-items/${assessmentTestId}`,
        method: 'PUT',
        body: updatedItem,
      }),
      invalidatesTags: (result, error, { assessmentTestId }) => [
        { type: 'TestItem', id: assessmentTestId },
        { type: 'TestItem', id: 'LIST' },
      ],
      async onQueryStarted(
        { reportId, assessmentTestId, assessmentId, ...patch },
        { dispatch, queryFulfilled }
      ) {
        // Optimistically update the getReportItems cache
        const patchResult = dispatch(
          inspectionsReportsAPI.util.updateQueryData(
            'getReportItems',
            String(reportId),
            (draft: ReportTestItem[]) => {
              const itemToUpdate = draft.find(
                (item) => item.assessmentTestId === assessmentTestId
              );
              if (itemToUpdate) {
                Object.assign(itemToUpdate, patch);
              }
            }
          )
        );

        try {
          const { data: updatedItem } = await queryFulfilled;

          // Optional: Update with server response if needed
          dispatch(
            inspectionsReportsAPI.util.updateQueryData(
              'getReportItems',
              String(reportId),
              (draft: ReportTestItem[]) => {
                const itemToUpdate = draft.find(
                  (item) => item.assessmentTestId === assessmentTestId
                );
                if (itemToUpdate) {
                  Object.assign(itemToUpdate, updatedItem);
                }
              }
            )
          );
        } catch {
          // Undo the optimistic update on error
          patchResult.undo();
        }
      },
    }),

    toggleTestSwitch: builder.mutation<
      ToggleSwitchResponse,
      ToggleSwitchRequest
    >({
      query: ({ reportId, revisionId, testItemId, switchName }) => ({
        url: `revisions/${revisionId}/test-items/${testItemId}/toggle/${switchName}`,
        method: 'POST',
      }),
      async onQueryStarted(
        { reportId, revisionId, testItemId, switchName },
        { dispatch, queryFulfilled, getState }
      ) {
        const patchResult = updateItemInReportCache(
          dispatch,
          getState,
          String(reportId),
          Number(testItemId),
          (item) => {
            // Toggle the switch value
            const newSwitchValue = !item.switches[switchName];

            // Recalculate hasFastFillQA if toggling fast_fill_accepted
            let newHasFastFillQA = item.hasFastFillQA;
            if (switchName === 'fast_fill_accepted') {
              newHasFastFillQA = item.hasUnmatchedSegments && !newSwitchValue;
            }

            // Determine if test is excluded (for exclusionReason calculation)
            const isExcluded =
              switchName === 'include'
                ? !newSwitchValue // If toggling include, use inverted new value
                : item.switches?.include === false; // Otherwise use existing include value

            // Recalculate exclusionReason with updated values
            const newExclusionReason = getExclusionReason(
              item.hasMissingData ?? false,
              newHasFastFillQA ?? false,
              item.status,
              isExcluded
            );

            return {
              ...item,
              switches: {
                ...item.switches,
                [switchName]: newSwitchValue,
              },
              hasFastFillQA: newHasFastFillQA,
              exclusionReason: newExclusionReason,
            };
          }
        );

        try {
          // Wait for the API response
          const { data } = await queryFulfilled;

          const currentData = inspectionsReportsAPI.endpoints.getReportItems.select(
            String(reportId)
          )(getState())?.data;
          if (currentData) {
            dispatch(
              inspectionsReportsAPI.util.upsertQueryData('getReportItems', String(reportId), [
                ...currentData,
              ])
            );
          }

          // If needed, you could update with the server response data here
          // This is optional if the optimistic update is sufficient
        } catch {
          // Revert all optimistic updates on error
          patchResult.undo();
        }
      },
    }),

    fastFillToggle: builder.mutation<
      {
        success: boolean;
        value: boolean;
        updated_at: string;
        updated_by: number;
      },
      { testItemId: string }
    >({
      query: ({ testItemId }) => ({
        url: `test-items/${testItemId}/fast-fill`,
        method: 'PUT',
      }),
      async onQueryStarted(
        { testItemId },
        { dispatch, queryFulfilled, getState }
      ) {
        const testItemIdNum = Number(testItemId);

        // Collect all patch results for rollback on error
        const allPatches: Array<{ undo: () => void }> = [];

        try {
          // 1. UPDATE ALL REPORT AND INSPECTION CACHES THAT CONTAIN THIS TEST ITEM
          const state = getState() as any;
          const apiCaches = state.assetApi.queries;

          Object.keys(apiCaches).forEach((cacheKey) => {
            const queryData = apiCaches[cacheKey];

            // Check if this is a getReportItems query with data
            if (
              queryData?.endpointName === 'getReportItems' &&
              queryData?.status === 'fulfilled' &&
              queryData?.data
            ) {
              const reportId = queryData.originalArgs;
              const items = queryData.data as ReportTestItem[];

              // Check if this report contains our test item
              const hasTestItem = items.some(
                (item) => item.assessmentTestId === testItemIdNum
              );

              if (hasTestItem) {
                const patchResult = dispatch(
                  inspectionsReportsAPI.util.updateQueryData(
                    'getReportItems',
                    reportId,
                    (draft) => {
                      const itemIndex = draft.findIndex(
                        (item) => item.assessmentTestId === testItemIdNum
                      );
                      if (itemIndex !== -1) {
                        const currentValue =
                          draft[itemIndex].switches?.fast_fill_accepted ??
                          false;
                        draft[itemIndex] = {
                          ...draft[itemIndex],
                          switches: {
                            ...draft[itemIndex].switches,
                            fast_fill_accepted: !currentValue,
                          },
                        };
                      }
                    }
                  )
                );
                allPatches.push(patchResult);
              }
            }

            // Check if this is a getInspectionTestItems query with data
            if (
              queryData?.endpointName === 'getInspectionTestItems' &&
              queryData?.status === 'fulfilled' &&
              queryData?.data
            ) {
              const inspectionId = queryData.originalArgs;
              const items = queryData.data as ReportTestItem[];

              const hasTestItem = items.some(
                (item) => item.assessmentTestId === testItemIdNum
              );

              if (hasTestItem) {
                const patchResult = dispatch(
                  testItemsAPI.util.updateQueryData(
                    'getInspectionTestItems',
                    inspectionId,
                    (draft) => {
                      const itemIndex = draft.findIndex(
                        (item) => item.assessmentTestId === testItemIdNum
                      );
                      if (itemIndex !== -1) {
                        const currentValue =
                          draft[itemIndex].switches?.fast_fill_accepted ??
                          false;
                        draft[itemIndex] = {
                          ...draft[itemIndex],
                          switches: {
                            ...draft[itemIndex].switches,
                            fast_fill_accepted: !currentValue,
                          },
                        };
                      }
                    }
                  )
                );
                allPatches.push(patchResult);
              }
            }
          });

          // 2. UPDATE SINGLE ITEM CACHES
          // Check all getSingleReportItem caches for this test item
          Object.keys(apiCaches).forEach((cacheKey) => {
            const queryData = apiCaches[cacheKey];

            if (
              queryData?.endpointName === 'getSingleReportItem' &&
              queryData?.status === 'fulfilled' &&
              queryData?.data &&
              queryData.data.assessmentTestId === testItemIdNum
            ) {
              const { reportId } = queryData.originalArgs;

              const patchResult = dispatch(
                inspectionsReportsAPI.util.updateQueryData(
                  'getSingleReportItem',
                  { reportId, testItemId: testItemIdNum },
                  (draft) => {
                    const currentValue =
                      draft.switches?.fast_fill_accepted ?? false;
                    draft.switches = {
                      ...draft.switches,
                      fast_fill_accepted: !currentValue,
                    };
                  }
                )
              );
              allPatches.push(patchResult);
            }
          });

          // 3. UPDATE INDIVIDUAL TEST ITEM CACHE (if exists)
          const testItemCacheKey = testItemIdNum;
          const testItemCache =
            apiCaches[`getTestItem(${JSON.stringify(testItemCacheKey)})`];

          if (testItemCache?.status === 'fulfilled' && testItemCache?.data) {
            const patchResult = dispatch(
              testItemsAPI.util.updateQueryData(
                'getTestItem',
                testItemCacheKey,
                (draft) => {
                  if ('switches' in draft) {
                    const currentValue =
                      (draft as any).switches?.fast_fill_accepted ?? false;
                    (draft as any).switches = {
                      ...(draft as any).switches,
                      fast_fill_accepted: !currentValue,
                    };
                  }
                }
              )
            );
            allPatches.push(patchResult);
          }

          // Wait for the API response
          const { data } = await queryFulfilled;

          // 4. UPDATE CACHES WITH SERVER RESPONSE (if needed)
          // The optimistic updates should be sufficient, but we sync with server data for consistency
          if (data.success) {
            // Update all caches with the actual server value
            Object.keys(apiCaches).forEach((cacheKey) => {
              const queryData = apiCaches[cacheKey];

              if (
                queryData?.endpointName === 'getReportItems' &&
                queryData?.status === 'fulfilled' &&
                queryData?.data
              ) {
                const reportId = queryData.originalArgs;
                const items = queryData.data as ReportTestItem[];
                if (
                  items.some((item) => item.assessmentTestId === testItemIdNum)
                ) {
                  dispatch(
                    inspectionsReportsAPI.util.updateQueryData(
                      'getReportItems',
                      reportId,
                      (draft) => {
                        const itemIndex = draft.findIndex(
                          (item) => item.assessmentTestId === testItemIdNum
                        );
                        if (itemIndex !== -1) {
                          draft[itemIndex].switches = {
                            ...draft[itemIndex].switches,
                            fast_fill_accepted: data.value,
                          };
                        }
                      }
                    )
                  );
                }
              }

              if (
                queryData?.endpointName === 'getInspectionTestItems' &&
                queryData?.status === 'fulfilled' &&
                queryData?.data
              ) {
                const inspectionId = queryData.originalArgs;
                const items = queryData.data as ReportTestItem[];
                if (
                  items.some((item) => item.assessmentTestId === testItemIdNum)
                ) {
                  dispatch(
                    testItemsAPI.util.updateQueryData(
                      'getInspectionTestItems',
                      inspectionId,
                      (draft) => {
                        const itemIndex = draft.findIndex(
                          (item) => item.assessmentTestId === testItemIdNum
                        );
                        if (itemIndex !== -1) {
                          draft[itemIndex].switches = {
                            ...draft[itemIndex].switches,
                            fast_fill_accepted: data.value,
                          };
                        }
                      }
                    )
                  );
                }
              }
            });

            // Update single item caches with server response
            Object.keys(apiCaches).forEach((cacheKey) => {
              const queryData = apiCaches[cacheKey];

              if (
                queryData?.endpointName === 'getSingleReportItem' &&
                queryData?.status === 'fulfilled' &&
                queryData?.data &&
                queryData.data.assessmentTestId === testItemIdNum
              ) {
                const { reportId } = queryData.originalArgs;

                dispatch(
                  inspectionsReportsAPI.util.updateQueryData(
                    'getSingleReportItem',
                    { reportId, testItemId: testItemIdNum },
                    (draft) => {
                      draft.switches = {
                        ...draft.switches,
                        fast_fill_accepted: data.value,
                      };
                    }
                  )
                );
              }
            });
          }
        } catch (error) {
          console.error(
            'fastFillToggle failed, reverting optimistic updates',
            error
          );

          // Revert all optimistic updates on error
          allPatches.forEach((patch) => patch.undo());
          throw error;
        }
      },

      // Invalidate relevant tags to ensure data consistency
      invalidatesTags: (result, error, { testItemId }) => {
        if (error) return [];

        const testItemIdNum = Number(testItemId);

        return [
          // Invalidate the specific test item
          { type: 'TestItem', id: testItemIdNum },

          // Invalidate all test item lists that might contain this item
          // This is a broad invalidation to ensure consistency across all reports
          { type: 'TestItems', id: 'LIST' },

          // Note: We don't know which specific reports contain this test item
          // without additional API calls, so we use broader invalidation
          // If performance becomes an issue, we could:
          // 1. Add a new endpoint to get all report IDs containing a test item
          // 2. Track which reports are currently cached and contain this item
          // 3. Use a more targeted invalidation strategy
        ];
      },
    }),

    getTestItemComments: builder.query<TestItemComment[], number>({
      query: (testItemId) => `tests/${testItemId}/comments`,
      transformResponse: (response: { data: TestItemComment[] } | TestItemComment[]) => {
        if (Array.isArray(response)) {
          return response;
        }
        return response.data || [];
      },
      providesTags: (result, error, testItemId) =>
        result
          ? [
              ...result.map(({ id }) => ({
                type: 'Comments' as const,
                id: `${testItemId}-${id}`,
              })),
              { type: 'Comments' as const, id: testItemId },
            ]
          : [{ type: 'Comments' as const, id: testItemId }],
    }),

    addTestItemComment: builder.mutation<
      TestItemComment,
      { testItemId: number; content: string }
    >({
      query: ({ testItemId, content }) => ({
        url: `tests/${testItemId}/comments`,
        method: 'POST',
        body: { content },
      }),
      invalidatesTags: (result, error, { testItemId }) => [
        { type: 'Comments', id: testItemId },
        { type: 'TestItem', id: testItemId },
      ],
    }),

    deleteTestItemComment: builder.mutation<
      void,
      { testItemId: number; commentId: number }
    >({
      query: ({ commentId }) => ({
        url: `comments/${commentId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { testItemId }) => [
        { type: 'Comments', id: testItemId },
        { type: 'TestItem', id: testItemId },
      ],
    }),

    setTestItemCommentResolved: builder.mutation<
      TestItemComment,
      { testItemId: number; commentId: number; resolved: boolean }
    >({
      query: ({ commentId, resolved }) => ({
        url: `comments/${commentId}/resolved`,
        method: 'PUT',
        body: { resolved },
      }),
      invalidatesTags: (result, error, { testItemId }) => [
        { type: 'Comments', id: testItemId },
        { type: 'TestItem', id: testItemId },
      ],
    }),
  }),
});

export const {
  useGetTestItemQuery,
  useCreateTestItemMutation,
  useUpdateTestItemMutation,
  useGetInspectionTestItemsQuery,
  useToggleTestSwitchMutation,
  useGetTestItemCommentsQuery,
  useAddTestItemCommentMutation,
  useDeleteTestItemCommentMutation,
  useSetTestItemCommentResolvedMutation,
  useUpdateReportTestItemMutation,
  useFastFillToggleMutation,
} = testItemsAPI;
